import { parsePhoneNumberFromString, getExampleNumber, CountryCode } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import RegionalDetection from "./regional-detection";

export interface PhoneParseResult {
  e164?: string;
  national?: string;
  country?: string; // ISO-3166 alpha-2
  error?: string;
}

/**
 * Country metadata for phone formatting and validation
 */
export interface CountryPhoneMetadata {
  countryCode: string; // ISO-3166 alpha-2
  dialCode: string; // e.g., "+44"
  name: string;
  expectedDigits: number; // Expected national number length (most common)
  placeholder: string; // Example without country code, formatted with spaces
  formatPattern: number[]; // Spacing pattern, e.g., [4, 3, 3] for "XXXX XXX XXX"
}

/**
 * Country phone metadata for all supported countries
 */
export const COUNTRY_PHONE_METADATA: Record<string, CountryPhoneMetadata> = {
  AE: { countryCode: "AE", dialCode: "+971", name: "United Arab Emirates", expectedDigits: 9, placeholder: "50 123 4567", formatPattern: [2, 3, 4] },
  AU: { countryCode: "AU", dialCode: "+61", name: "Australia", expectedDigits: 9, placeholder: "412 345 678", formatPattern: [3, 3, 3] },
  BR: { countryCode: "BR", dialCode: "+55", name: "Brazil", expectedDigits: 11, placeholder: "11 91234 5678", formatPattern: [2, 5, 4] },
  CA: { countryCode: "CA", dialCode: "+1", name: "Canada", expectedDigits: 10, placeholder: "416 123 4567", formatPattern: [3, 3, 4] },
  CN: { countryCode: "CN", dialCode: "+86", name: "China", expectedDigits: 11, placeholder: "131 2345 6789", formatPattern: [3, 4, 4] },
  DE: { countryCode: "DE", dialCode: "+49", name: "Germany", expectedDigits: 10, placeholder: "151 12345678", formatPattern: [3, 8] },
  ES: { countryCode: "ES", dialCode: "+34", name: "Spain", expectedDigits: 9, placeholder: "612 34 56 78", formatPattern: [3, 2, 2, 2] },
  FR: { countryCode: "FR", dialCode: "+33", name: "France", expectedDigits: 9, placeholder: "6 12 34 56 78", formatPattern: [1, 2, 2, 2, 2] },
  GB: { countryCode: "GB", dialCode: "+44", name: "United Kingdom", expectedDigits: 10, placeholder: "7700 900 123", formatPattern: [4, 3, 3] },
  HK: { countryCode: "HK", dialCode: "+852", name: "Hong Kong", expectedDigits: 8, placeholder: "5123 4567", formatPattern: [4, 4] },
  IN: { countryCode: "IN", dialCode: "+91", name: "India", expectedDigits: 10, placeholder: "81234 56789", formatPattern: [5, 5] },
  IT: { countryCode: "IT", dialCode: "+39", name: "Italy", expectedDigits: 10, placeholder: "312 345 6789", formatPattern: [3, 3, 4] },
  JP: { countryCode: "JP", dialCode: "+81", name: "Japan", expectedDigits: 10, placeholder: "90 1234 5678", formatPattern: [2, 4, 4] },
  MX: { countryCode: "MX", dialCode: "+52", name: "Mexico", expectedDigits: 10, placeholder: "55 1234 5678", formatPattern: [2, 4, 4] },
  NZ: { countryCode: "NZ", dialCode: "+64", name: "New Zealand", expectedDigits: 9, placeholder: "21 123 4567", formatPattern: [2, 3, 4] },
  SG: { countryCode: "SG", dialCode: "+65", name: "Singapore", expectedDigits: 8, placeholder: "8123 4567", formatPattern: [4, 4] },
  US: { countryCode: "US", dialCode: "+1", name: "United States", expectedDigits: 10, placeholder: "415 123 4567", formatPattern: [3, 3, 4] },
};

/**
 * Validation result with structured error information
 */
export interface PhoneValidationResult {
  valid: boolean;
  error?: {
    country: string;
    expectedDigits: number;
    mask: string;
    example: string;
  };
}

/**
 * Format phone number with country-specific spacing pattern
 * @param digits - Phone number digits only (no spaces or formatting)
 * @param countryCode - ISO-3166 alpha-2 country code
 * @returns Formatted phone number with spaces
 */
export function formatPhoneNumber(digits: string, countryCode: string): string {
  const metadata = COUNTRY_PHONE_METADATA[countryCode];
  if (!metadata) {
    return digits; // No formatting for unknown countries
  }

  const { formatPattern, expectedDigits } = metadata;
  const cleanDigits = digits.replace(/\D/g, "");
  
  // Truncate to expected length
  const limitedDigits = cleanDigits.substring(0, expectedDigits);
  
  let formatted = "";
  let position = 0;
  
  for (const groupSize of formatPattern) {
    if (position >= limitedDigits.length) break;
    
    if (formatted.length > 0) {
      formatted += " ";
    }
    
    formatted += limitedDigits.substring(position, position + groupSize);
    position += groupSize;
  }
  
  // Add any remaining digits (up to expected length)
  if (position < limitedDigits.length) {
    if (formatted.length > 0) {
      formatted += " ";
    }
    formatted += limitedDigits.substring(position);
  }
  
  return formatted;
}

/**
 * Validate phone number for a specific country
 * @param phoneNumber - Phone number (can include spaces or formatting)
 * @param countryCode - ISO-3166 alpha-2 country code
 * @returns Validation result with structured error information
 */
export function validatePhoneForCountry(phoneNumber: string, countryCode: string): PhoneValidationResult {
  const metadata = COUNTRY_PHONE_METADATA[countryCode];
  
  if (!metadata) {
    return {
      valid: false,
      error: {
        country: countryCode,
        expectedDigits: 10, // Fallback
        mask: "XXXXXXXXXX",
        example: "1234567890",
      },
    };
  }

  // Strip all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  
  // Use libphonenumber-js for comprehensive validation
  try {
    const phone = parsePhoneNumberFromString(digitsOnly, countryCode as CountryCode);
    
    if (phone && phone.isValid()) {
      return { valid: true };
    }
  } catch (e) {
    // Fall through to error
  }

  // Generate error with country-specific information
  const mask = metadata.formatPattern.map((size) => "X".repeat(size)).join(" ");
  
  return {
    valid: false,
    error: {
      country: metadata.name,
      expectedDigits: metadata.expectedDigits,
      mask,
      example: metadata.placeholder,
    },
  };
}

/**
 * Normalize and validate a phone number input to E.164.
 * - Accepts `+` and digits
 * - Uses detected country when input is not in E.164
 */
export async function parseAndValidatePhone(input: string, countryOverride?: string): Promise<PhoneParseResult> {
  const trimmed = (input || "").trim();
  if (!trimmed) {
    return { error: "phone.empty" };
  }

  // Allow only + and digits visually; parsing lib will validate further
  const cleaned = trimmed.replace(/[^+\d]/g, "");

  // Detect default country for non-E.164 input
  let defaultCountry: string | undefined = countryOverride;
  try {
    if (!defaultCountry) {
      const detection = await RegionalDetection.detectUserRegion();
      defaultCountry = detection?.countryCode;
    }
  } catch {}

  const hasPlus = cleaned.startsWith("+");
  const phone = hasPlus
    ? parsePhoneNumberFromString(cleaned)
    : defaultCountry
    ? parsePhoneNumberFromString(cleaned, defaultCountry as any)
    : parsePhoneNumberFromString(cleaned);

  if (!phone) {
    return { error: "phone.invalid_format" };
  }

  // Ensure it's valid and possible for the region
  if (!phone.isValid()) {
    return { error: "phone.invalid_number" };
  }

  return {
    e164: phone.number, // already E.164
    national: phone.formatNational(),
    country: phone.country,
  };
}

/**
 * Format E.164 phone number to human-readable format
 * E.g., +15551234567 -> (555) 123-4567
 */
export function formatPhoneDisplay(e164Phone?: string): string {
  if (!e164Phone) return "-";
  
  try {
    const phone = parsePhoneNumberFromString(e164Phone);
    if (!phone) return e164Phone;
    
    return phone.formatNational();
  } catch {
    return e164Phone;
  }
}
